import { useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { FileUpload } from "primereact/fileupload";
import type { FileUploadHeaderTemplateOptions, ItemTemplateOptions } from "primereact/fileupload";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { useToast } from "sj-ui-toolkit/src/hooks";
import Papa from "papaparse";
import { isEmpty } from "lodash";
import type { DirectoryUserShape } from "@/api/assignments";
import type { AvailableUsersMap as MapShape } from "@/hooks/useAvailableUsers";

interface CsvUploadModalProps {
  open: boolean;
  onClose: (open: false) => void;
  availableUsers: DirectoryUserShape[];
  onUsersAdded: (users: DirectoryUserShape[]) => void;
  addedUsers?: DirectoryUserShape[];
  allUsers?: Map<string, MapShape>;
}

const CsvUploadModal = ({
  open,
  onClose,
  availableUsers,
  onUsersAdded,
  addedUsers = [],
  allUsers = new Map(),
}: CsvUploadModalProps) => {
  const { showToast } = useToast();
  const fileUploadRef = useRef<FileUpload>(null);

  const [csvFiles, setCsvFiles] = useState<File[]>([]);
  const [csvErrorMessages, setCsvErrorMessages] = useState<string[]>([]);

  const handleCloseClick = () => {
    setCsvFiles([]);
    setCsvErrorMessages([]);
    onClose(false);
  };

  const onTemplateRemove = (_file: File, callback: () => void) => {
    setCsvFiles([]);
    setCsvErrorMessages([]);
    callback();
  };

  const getUserText = (count: number) => (count === 1 ? "user" : "users");
  const getVerbText = (count: number) => (count === 1 ? "has" : "have");

  const handleCsvUpload = () => {
    const file = csvFiles[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = e => {
      try {
        const csvData = e.target?.result;
        if (typeof csvData !== "string") return;

        Papa.parse<Record<string, string>>(csvData, {
          header: true,
          complete: results => {
            setCsvErrorMessages([]);

            if (!results?.data || !Array.isArray(results.data)) {
              setCsvErrorMessages(["CSV file appears to be empty or malformed"]);
              return;
            }

            const firstRow = results.data[0];
            const firstColumnKey = firstRow ? Object.keys(firstRow)[0] : undefined;
            if (!firstColumnKey) {
              setCsvErrorMessages(["CSV file appears to be empty"]);
              return;
            }

            const errors: string[] = [];
            const extractedEmails: { email: string; lineNumber: number }[] = [];

            results.data.forEach((row, index) => {
              const email = row[firstColumnKey]?.toString().trim();
              const lineNumber = index + 2;
              if (!email) return;

              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              if (!emailRegex.test(email)) {
                errors.push(`Invalid email format on line: ${lineNumber}`);
              } else {
                extractedEmails.push({ email, lineNumber });
              }
            });

            const usersToAdd: DirectoryUserShape[] = [];
            const notFoundEmails: string[] = [];

            extractedEmails.forEach(({ email, lineNumber }) => {
              const normalizedEmail = email.toLowerCase().trim();
              const user = availableUsers.find(u => u.email?.toLowerCase() === normalizedEmail);
              const isAlreadyAdded = addedUsers.some(
                u => u.email?.toLowerCase() === normalizedEmail
              );

              if (isAlreadyAdded) {
                // skip silently
              } else if (user) {
                usersToAdd.push(user);
              } else {
                const userStatus = allUsers.get(normalizedEmail);
                if (userStatus?.archived) {
                  notFoundEmails.push(`User is archived: ${email} (line: ${lineNumber})`);
                } else if (userStatus && !userStatus.licensed) {
                  notFoundEmails.push(`User is not licensed: ${email} (line: ${lineNumber})`);
                } else {
                  notFoundEmails.push(`User not found: ${email} (line: ${lineNumber})`);
                }
              }
            });

            const errorMessages = [...errors, ...notFoundEmails];

            if (usersToAdd.length > 0) {
              onUsersAdded(usersToAdd);
              showToast({
                severity: "success",
                summary: "Success",
                detail: `${usersToAdd.length} ${getUserText(usersToAdd.length)} ${getVerbText(usersToAdd.length)} been added`,
              });

              if (errorMessages.length > 0) {
                setCsvErrorMessages(errorMessages);
              } else {
                setTimeout(() => onClose(false), 1000);
              }
            } else {
              setCsvErrorMessages(
                errorMessages.length > 0 ? errorMessages : ["No valid users found to add"]
              );
            }
          },
          error: (error: { message: string }) => {
            setCsvErrorMessages([`Error parsing CSV: ${error.message}`]);
          },
        });
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        setCsvErrorMessages([`Error processing CSV file: ${msg}`]);
      }
    };
    reader.onerror = () => {
      setCsvErrorMessages(["Error reading file"]);
    };
    reader.readAsText(file);
  };

  const footerTemplate = () => (
    <div className="flex justify-end gap-2">
      <Button
        type="button"
        icon="pi pi-times"
        label="Cancel"
        data-testid="cancel-add-users-button"
        severity="secondary"
        onClick={handleCloseClick}
      />
      <Button
        type="button"
        data-testid="add-users-button"
        label="Add Users"
        icon="pi pi-user-plus"
        disabled={isEmpty(csvFiles)}
        onClick={handleCsvUpload}
      />
    </div>
  );

  const downloadCsvTemplate = () => {
    const csvData = `Users for assignment\nexisting_user1@example.com\nexisting_user2@example.com\nexisting_user3@example.com\nexisting_user4@example.com`;
    const blob = new Blob([csvData], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "users_sample.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const chooseOptions = {
    icon: "pi pi-cloud-upload",
    iconOnly: false,
    label: "Upload CSV",
    className: "p-button-primary",
  };

  const headerTemplate = (options: FileUploadHeaderTemplateOptions) => {
    const { className, chooseButton } = options;
    return (
      <div
        className={className}
        style={{ backgroundColor: "transparent", display: "flex", alignItems: "center" }}
      >
        {chooseButton}
        <small className="ml-2">
          Need help? Download a sample CSV file{" "}
          <Button
            onClick={downloadCsvTemplate}
            unstyled
            className="text-primary hover:underline hover:cursor-pointer"
          >
            template
          </Button>
          .
        </small>
      </div>
    );
  };

  const itemTemplate = (file: object, props: ItemTemplateOptions) => {
    const f = file as File;
    return (
      <div>
        <div className="flex justify-center items-center flex-wrap max-w-xl gap-4 mx-auto min-h-26">
          <strong className="my-auto">{f.name}</strong>
          <Tag value={props.formatSize} severity="success" className="mr-4" />
          <Button
            type="button"
            icon="pi pi-times"
            severity="secondary"
            className="p-button-rounded"
            onClick={e => onTemplateRemove(f, () => props.onRemove(e))}
          />
        </div>
        <div className="mt-6 text-left max-h-42 overflow-y-auto">
          {csvErrorMessages.map((message, index) => (
            <p className="text-error body-xs" key={index}>
              {message}
            </p>
          ))}
        </div>
      </div>
    );
  };

  const emptyTemplate = () => (
    <div className="flex items-center justify-center flex-col">
      <i
        className="pi pi-cloud-upload mt-3 p-5 rounded-full"
        style={{ fontSize: "5em", backgroundColor: "var(--surface-b)", color: "var(--surface-d)" }}
      />
    </div>
  );

  return (
    <Dialog
      header="CSV file"
      data-testid="csv-upload-modal"
      visible={open}
      onHide={handleCloseClick}
      footer={footerTemplate}
      className="w-[50rem]"
      blockScroll
      maskStyle={{ backdropFilter: "blur(5px)" }}
    >
      <div className="mt-4 mb-8">
        <FileUpload
          ref={fileUploadRef}
          name="csvFile"
          multiple={false}
          accept="text/csv"
          maxFileSize={1000000}
          customUpload
          onSelect={e => setCsvFiles(e.files)}
          onClear={() => {
            setCsvFiles([]);
            setCsvErrorMessages([]);
          }}
          uploadHandler={handleCsvUpload}
          headerTemplate={headerTemplate}
          itemTemplate={itemTemplate}
          emptyTemplate={emptyTemplate}
          chooseOptions={chooseOptions}
          progressBarTemplate={<></>}
        />
      </div>
    </Dialog>
  );
};

export default CsvUploadModal;
